/**
 * Statuses describing what happened to a session's location, from "never asked" to "blue dot showing".
 *  - requested: location request started (the browser may now prompt the user)
 *  - skipped:  device or venue unsupported, so location was never requested (reason: device | venue)
 *  - disabled: user denied location permission
 *  - error:    geolocation failed for another reason (errorCode: 2 = unavailable, 3 = timeout)
 *  - off_site: reading received, but outside the venue bounds
 *  - no_floor: reading inside the venue bounds, but no floor resolved (reason: no_floor_level | floor_not_found)
 *  - showing:  floor resolved and the blue dot is displayed
 */
export type LocationStatus = 'requested' | 'skipped' | 'disabled' | 'error' | 'off_site' | 'no_floor' | 'showing';

export type LocationStatusReporter = (status: LocationStatus, properties?: Record<string, unknown>) => void;

/**
 * Creates a reporter that sends a `bluedot/locationStatus` App Insights event, at most once per
 * distinct status + reason per session.
 */
export function createLocationStatusReporter(app): LocationStatusReporter {
  const reported = new Set<string>();
  return (status, properties = {}) => {
    const key = `${status}:${properties.reason ?? ''}`;
    if (reported.has(key)) {
      return;
    }
    reported.add(key);
    app.bus.send('appInsights/log', { name: 'bluedot/locationStatus', properties: { status, ...properties } });
  };
}
